/** Тема портала: «Как в системе» / «Светлая» / «Тёмная».
 *
 *  Tailwind `darkMode: ["class"]` — тёмная тема = класс `dark` на <html>; палитры токенов — `index.css`
 *  (`:root` и `.dark`). Выбор пользователя — в localStorage (`THEME_STORAGE_KEY`); `system` следует
 *  `prefers-color-scheme` (iPhone «Автоматически» — вечером тёмная).
 *
 *  ⚠️ Встроенный скрипт в `index.html` повторяет `resolveTheme` + `applyTheme` до первой отрисовки (без
 *  вспышки светлой темы): ключ, режимы и цвета `THEME_COLORS` — менять в обоих местах; сверяет
 *  `theme-inline-script.test.ts`. */

export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "theme";
export const THEME_MODES: readonly ThemeMode[] = ["system", "light", "dark"];
export const THEME_LABELS: Record<ThemeMode, string> = {
  system: "Как в системе",
  light: "Светлая",
  dark: "Тёмная",
};
export const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

/** Цвет панели Safari (`<meta name="theme-color">`) = `--background` из `index.css` в hex. */
export const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: "#ffffff", // 0 0% 100%
  dark: "#020817", // 222.2 84% 4.9%
};

export function parseThemeMode(raw: string | null): ThemeMode | undefined {
  return THEME_MODES.find((m) => m === raw);
}

/** Сохранённый выбор. Нет записи или устаревшее значение — штатно `system`; отказ хранилища (приватное
 *  окно, запрет сайту хранить данные) — тоже `system`, но с предупреждением в консоли. */
export function readStoredThemeMode(): ThemeMode {
  try {
    return parseThemeMode(localStorage.getItem(THEME_STORAGE_KEY)) ?? "system";
  } catch (e) {
    console.warn("[theme] выбор темы не прочитан из localStorage", e);
    return "system";
  }
}

/** Запись выбора; при отказе хранилища тема всё равно переключается — до перезагрузки страницы. */
export function writeStoredThemeMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch (e) {
    console.warn(
      "[theme] выбор темы не записан в localStorage — действует до перезагрузки",
      e,
    );
  }
}

export function systemPrefersDark(): boolean {
  return window.matchMedia(DARK_MEDIA_QUERY).matches;
}

export function resolveTheme(
  mode: ThemeMode,
  prefersDark: boolean,
): ResolvedTheme {
  return mode === "system" ? (prefersDark ? "dark" : "light") : mode;
}

/** Класс `dark` на <html>, `color-scheme` (полосы прокрутки, нативные поля дат) и цвет панели Safari. */
export function applyTheme(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.style.colorScheme = theme;
  document
    .querySelectorAll('meta[name="theme-color"]')
    .forEach((m) => m.setAttribute("content", THEME_COLORS[theme]));
}
